import { FormTextInput } from "stentor-models";

/**
 * The HTML input helper attributes we derive for a form field.
 *
 * These are what give mobile users the right keyboard and let iOS/Android
 * AutoFill offer the right saved value - neither happens without explicit tokens.
 */
export interface InputHelperProps {
    /**
     * The real HTML input type. Note this is NOT FormInput.type, which is the
     * widget discriminator ("TEXT" | "DROPDOWN" | ...) and is not a valid input type.
     */
    readonly type: "text" | "tel" | "email";
    readonly inputMode: "text" | "tel" | "email" | "numeric";
    /**
     * Left off entirely when we cannot confidently pick a token - a wrong
     * autocomplete is worse than none.
     */
    readonly autoComplete?: string;
    readonly enterKeyHint: "next" | "done";
}

export interface FormatToInputPropsRequest {
    readonly format?: FormTextInput["format"];
    readonly name?: string;
    /**
     * When true the keyboard shows "done" rather than "next".
     */
    readonly isLastField?: boolean;
}

type FormatMapping = Omit<InputHelperProps, "enterKeyHint">;

const FORMAT_PROPS: Record<NonNullable<FormTextInput["format"]>, FormatMapping> = {
    PHONE: { type: "tel", inputMode: "tel", autoComplete: "tel" },
    EMAIL: { type: "email", inputMode: "email", autoComplete: "email" },
    // Deliberately not type="number" - that adds spinners and strips the leading
    // zero from real US postal codes such as 02134.
    ZIP_CODE: { type: "text", inputMode: "numeric", autoComplete: "postal-code" },
    ADDRESS: { type: "text", inputMode: "text", autoComplete: "street-address" },
};

const DEFAULT_PROPS: FormatMapping = { type: "text", inputMode: "text" };

/**
 * Field names we can map to an autocomplete token without guessing. Keys are
 * normalized - lowercased with everything but letters and digits removed - so
 * "FULL_NAME", "fullName" and "full name" all land on the same entry.
 *
 * Keep this conservative. Anything ambiguous belongs on field.format instead.
 */
const NAME_AUTOCOMPLETE: Record<string, string> = {
    name: "name",
    fullname: "name",
    yourname: "name",
    customername: "name",
    contactname: "name",
    firstname: "given-name",
    fname: "given-name",
    givenname: "given-name",
    lastname: "family-name",
    lname: "family-name",
    surname: "family-name",
    familyname: "family-name",
    email: "email",
    emailaddress: "email",
    phone: "tel",
    phonenumber: "tel",
    telephone: "tel",
    tel: "tel",
    mobile: "tel",
    mobilephone: "tel",
    cellphone: "tel",
    zip: "postal-code",
    zipcode: "postal-code",
    postalcode: "postal-code",
    postcode: "postal-code",
    address: "street-address",
    streetaddress: "street-address",
    street: "street-address",
    city: "address-level2",
    town: "address-level2",
    state: "address-level1",
    province: "address-level1",
    company: "organization",
    companyname: "organization",
    organization: "organization",
    business: "organization",
    businessname: "organization",
};

function autoCompleteFromName(name?: string): string | undefined {
    if (!name) {
        return undefined;
    }

    return NAME_AUTOCOMPLETE[name.toLowerCase().replace(/[^a-z0-9]/g, "")];
}

/**
 * Derive the mobile input helper attributes for a text field.
 *
 * `field.format` carries the real semantics ("PHONE", "ZIP_CODE", ...); when it
 * is unset we fall back to plain text and only infer autoComplete from the field
 * name where that is unambiguous.
 */
export function formatToInputProps(request: FormatToInputPropsRequest): InputHelperProps {
    const { format, name, isLastField } = request;

    const mapped = format ? FORMAT_PROPS[format] : undefined;
    const enterKeyHint = isLastField ? "done" : "next";

    if (mapped) {
        return { ...mapped, enterKeyHint };
    }

    const autoComplete = autoCompleteFromName(name);

    return autoComplete
        ? { ...DEFAULT_PROPS, autoComplete, enterKeyHint }
        : { ...DEFAULT_PROPS, enterKeyHint };
}

export default formatToInputProps;
